import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import Image from 'components/image/Image';
import { IJobCard, IJobCardMatches } from 'core/interface/jobCard';
import { JobCardActionEnum, JobCardStatusEnum } from 'core/enum/jobCardAction';
import { JobService } from 'services/jobs/job';
import { Role } from 'core/enum/role';
import {
  calculateMatchPercentage,
  getMatchingScores,
  consolidateCategories
} from 'helpers/teaserView';

import './Index.scss';
import { DynamicActionButton } from 'components/newButtons/DynamicActionButton';
import { getDateFormatter } from 'helpers/dateConverter';
import { clampText } from 'helpers/sharedFunctions';
import useLocales from 'hooks/useLocales';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import ApplicationHistoryList from 'components/shared/ApplicationHistoryList/Index';
import { getCultureFitLabel } from 'helpers/cultureFitLabel';
import { formatTimestampedLine } from 'helpers/applicationHistory';

const JobCard: FC<IJobCard> = ({
  id,
  jobId,
  actions,
  icon,
  sub_title,
  date,
  postedAt,
  history,
  title,
  matches,
  messageAndResponse,
  profileType,
  status,
  isApplied,
  assessmentRequired,
  onActionComplete,
  competencies,
  competencyMinScores,
  cultureCompetencies,
  cultureProfile,
  style
}): JSX.Element => {
  const { t } = useLocales();

  const getActionLabel = (action: JobCardActionEnum): string => {
    switch (action) {
      case JobCardActionEnum.CANCEL:
        return 'Cancel';
      case JobCardActionEnum.WITHDRAW:
        return 'Withdraw';
      case JobCardActionEnum.REJECT:
        return 'Reject';
      case JobCardActionEnum.ACCEPT:
        return 'Accept';
      case JobCardActionEnum.APPLY:
        return 'Apply';
      case JobCardActionEnum.MORE_INFO:
        return 'More Info';
      case JobCardActionEnum.PROFILE_VIEW:
        return 'Profile';
      default:
        return '';
    }
  };

  const navigate = useNavigate();
  const [disableActions, setDisableActions] = useState<boolean>(false);
  const [hasAssessment, setHasAssessment] = useState<boolean>(false);
  const [calculatedMatches, setCalculatedMatches] = useState<IJobCardMatches[]>(
    []
  );

  const { user } = useSelector((state: RootState) => state.user);

  const assessment = useSelector((state: RootState) => state.assessment);

  const isCandidate = user?.role === Role.CANDIDATE;
  const isCompany =
    user?.role === Role.FIND_TALENT || user?.role === Role.ADMIN;

  useEffect(() => {
    if (isCandidate) {
      // Check if candidate has assessment results
      let lastAssessment = null;
      for (let i = 0; i < assessment?.assessmentData?.length; i++) {
        const assessmentReport = assessment?.assessmentData[i]?.report;
        if (assessmentReport) {
          lastAssessment = assessmentReport;
          break;
        }
      }

      setHasAssessment(!!lastAssessment);

      // If we have assessment results and job data, calculate actual matches
      if (
        lastAssessment &&
        competencies &&
        competencyMinScores &&
        cultureCompetencies
      ) {
        try {
          // Transform the assessment data into the right format
          const consolidatedCompetencies = consolidateCategories(
            lastAssessment.competencies
          );

          // Get matching scores for job competencies
          const talentScores = getMatchingScores(
            competencies,
            consolidatedCompetencies
          );
          const talentMatch = calculateMatchPercentage(
            talentScores,
            competencyMinScores
          );

          // Get matching scores for culture competencies
          const cultureScores = getMatchingScores(
            cultureCompetencies,
            consolidatedCompetencies
          );
          const cultureMinScores = cultureCompetencies.map(
            (item) => item.min_score
          );
          const cultureMatch = calculateMatchPercentage(
            cultureScores,
            cultureMinScores
          );

          // Only update if we have valid matches
          if (talentMatch !== null && cultureMatch !== null) {
            setCalculatedMatches([
              { percent: Number(talentMatch), title: 'Talent Match' },
              { percent: Number(cultureMatch), title: 'Cultural Fit' }
            ]);
          }
        } catch (error) {
          console.error('Error calculating matches:', error);
          // Keep the original matches if calculation fails
          setCalculatedMatches(matches || []);
        }
      } else if (matches) {
        // If we have matches from props, use them
        setCalculatedMatches(matches);
      } else {
        // If no matches and no assessment, set default values
        setCalculatedMatches([
          { percent: 0, title: 'Talent Match' },
          { percent: 0, title: 'Cultural Fit' }
        ]);
      }
    } else {
      // For non-candidates, use the matches from props or default to 0
      setCalculatedMatches(
        matches || [
          { percent: 0, title: 'Talent Match' },
          { percent: 0, title: 'Cultural Fit' }
        ]
      );
    }
  }, [
    assessment,
    competencies,
    competencyMinScores,
    cultureCompetencies,
    isCandidate,
    assessmentRequired,
    matches
  ]);

  const cancelActions = [
    JobCardActionEnum.CANCEL,
    JobCardActionEnum.WITHDRAW,
    JobCardActionEnum.REJECT
  ];

  const acceptActions = [JobCardActionEnum.ACCEPT, JobCardActionEnum.APPLY];

  const getAvailableActions = () => {
    if (!actions) return [];

    if (isCandidate) {
      return actions.filter((action) =>
        [
          JobCardActionEnum.MORE_INFO,
          ...acceptActions,
          ...cancelActions
        ].includes(action)
      );
    }

    if (isCompany) {
      return actions.filter((action) =>
        [JobCardActionEnum.MORE_INFO, JobCardActionEnum.PROFILE_VIEW].includes(
          action
        )
      );
    }

    return [];
  };

  const handleActionClick = async (action: string): Promise<void> => {
    if (disableActions) return;

    switch (action) {
      case JobCardActionEnum.MORE_INFO:
        navigate(`/job/${jobId || id}`);
        break;

      case JobCardActionEnum.PROFILE_VIEW:
        if (isCompany) {
          navigate(`/company/find-candidate/${jobId || id}`);
        }
        break;
      default:
        if (cancelActions.includes(action as JobCardActionEnum)) {
          if (isCandidate) {
            await reject();
          }
          break;
        } else if (acceptActions.includes(action as JobCardActionEnum)) {
          if (isCandidate) {
            await applyForAJob();
          }
        }
        break;
    }
  };

  const applyForAJob = async (): Promise<void> => {
    if (!isCandidate) return;
    setDisableActions(true);
    try {
      await JobService.applyForAJob(id);
      onActionComplete?.();
    } finally {
      setDisableActions(false);
    }
  };

  const reject = async (): Promise<void> => {
    if (!isCandidate) return;
    const pathName = window.location.pathname;
    if (!pathName.startsWith(CANDIDATE_ROUTES.FIND_JOBS)) {
      await JobService.rejectApplication(id);
      onActionComplete?.();
    }
  };

  return (
    <div className="jCard" style={style}>
      <div className="jCard__left">
        <Image
          name={icon}
          url={icon}
          className="jCard__left-image"
          width={64}
          height={45}
        />
        <div className="jCard__left__info">
          <span className="jCard__left__info-title" style={{ marginBottom: '4px' }}>
            {title}
            {profileType && (
              <span className="jCard__left__info-title-type">
                {profileType}
              </span>
            )}
          </span>
          {postedAt && (
            <span className="jCard__left__info-posted" style={{ marginBottom: '8px' }}>
              {getDateFormatter(postedAt, 'MMM DD YYYY')}
            </span>
          )}
          <span className="jCard__left__info-sub">
            {clampText(sub_title, 35)}
          </span>
        </div>
      </div>
      <div className="jCard__middle">
        <div className="jCard__middle__percents">
          {isCandidate && assessmentRequired && !hasAssessment ? (
            <div className="jCard__middle__assessment-required">
              {t('Assessment required to view match scores')}
            </div>
          ) : (
            calculatedMatches?.map((match, index) => (
              <div key={index} className="jCard__middle__matches">
                <span className="jCard__middle__matches-title">
                  {match.title === 'Cultural Fit'
                    ? getCultureFitLabel(match.percent, t)
                    : `${match.percent}% ${match.title}`}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="jCard__middle__details">
          {date && (
            <div className="jCard__middle__details__item">
              <span className="jCard__middle__details__item-desc">
                {formatTimestampedLine('Applied on', date, t, {
                  includeOn: false
                })}
              </span>
            </div>
          )}

          <ApplicationHistoryList history={history} classPrefix="jCard" t={t} />
        </div>
      </div>
      <div className="jCard__right">
        {assessmentRequired && !hasAssessment && (
          <div className="jCard__right__badge">{t('Assessment Required')}</div>
        )}
        {getAvailableActions().map((action, index) => (
          <div key={index} className="jCard__right__actions">
            {acceptActions.includes(action) &&
              (isApplied ? (
                <DynamicActionButton
                  label={t('Apply')}
                  backgroundColor="#0fb398"
                  onClick={() => {}}
                  isDisabled={true}
                  tooltip={t('You have already applied for this job')}
                />
              ) : (
                (!assessmentRequired ||
                  calculatedMatches?.some((e) => e.percent !== 0)) && (
                  <DynamicActionButton
                    label={getActionLabel(action)}
                    backgroundColor="#0fb398"
                    onClick={() => handleActionClick(JobCardActionEnum.ACCEPT)}
                    isDisabled={disableActions}
                  />
                )
              ))}
            {action === JobCardActionEnum.MORE_INFO && (
              <DynamicActionButton
                label={getActionLabel(action)}
                backgroundColor="#BEA0E5"
                onClick={() => handleActionClick(JobCardActionEnum.MORE_INFO)}
                isDisabled={disableActions}
              />
            )}
            {action === JobCardActionEnum.PROFILE_VIEW && (
              <DynamicActionButton
                label={getActionLabel(action)}
                backgroundColor="#A0D5E5"
                onClick={() =>
                  handleActionClick(JobCardActionEnum.PROFILE_VIEW)
                }
                isDisabled={disableActions}
              />
            )}
            {cancelActions.includes(action) &&
              status !== JobCardStatusEnum.ACCEPTED &&
              status !== JobCardStatusEnum.REJECTED && (
                <DynamicActionButton
                  label={getActionLabel(action)}
                  backgroundColor="red"
                  onClick={() => handleActionClick(action)}
                  isDisabled={disableActions}
                />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobCard;
